import { useEffect, useState } from "react";
import Cookie from "cookie-universal";
import { Axios } from "../../../Api/axios";
import { ADMIN_ALL_ADMINS, ADMIN_ADD_ADMIN, ADMIN_DELETE_ADMIN } from "../../../Api/Api";

const cookie = Cookie();

function RoleBadge({ role }) {
  const isSA = role === "superadmin";
  return (
    <span style={{
      display: "inline-block",
      fontSize: "0.72rem", fontWeight: 700,
      color: isSA ? "#d97706" : "#6b7280",
      background: isSA ? "#fffbeb" : "#f3f4f6",
      borderRadius: 50, padding: "2px 10px",
    }}>
      {isSA ? "سوبر أدمن" : "أدمن"}
    </span>
  );
}

export default function Settings() {
  const currentRole = cookie.get("role") || "admin";
  const currentUser = cookie.get("user_name") || "";
  const isSuperAdmin = currentRole === "superadmin";

  /* ── admins list (superadmin only) ── */
  const [admins,        setAdmins]        = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting,      setDeleting]      = useState(false);

  /* ── add form ── */
  const [form,       setForm]       = useState({ user_name: "", password: "", Role: "admin" });
  const [adding,     setAdding]     = useState(false);
  const [addErr,     setAddErr]     = useState("");
  const [addSuccess, setAddSuccess] = useState("");
  const [showPwd,    setShowPwd]    = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) return;
    setLoadingAdmins(true);
    Axios.get(ADMIN_ALL_ADMINS)
      .then((r) => setAdmins(r.data.admins || []))
      .catch(() => {})
      .finally(() => setLoadingAdmins(false));
  }, [isSuperAdmin]);

  async function handleAdd(e) {
    e.preventDefault();
    setAddErr(""); setAddSuccess("");
    if (!form.user_name.trim() || !form.password.trim()) {
      setAddErr("جميع الحقول مطلوبة"); return;
    }
    if (form.password.length < 6) {
      setAddErr("كلمة المرور يجب أن تكون 6 أحرف على الأقل"); return;
    }
    setAdding(true);
    try {
      const res = await Axios.post(ADMIN_ADD_ADMIN, {
        user_name: form.user_name.trim(),
        password:  form.password,
        Role:      form.Role,
      });
      const newAdmin = res.data.admin;
      if (isSuperAdmin) {
        setAdmins((prev) => [...prev, { ...newAdmin, createdAt: new Date().toISOString() }]);
      }
      setForm({ user_name: "", password: "", Role: "admin" });
      setAddSuccess(`تم إضافة المدير "${newAdmin.user_name}" بنجاح`);
      setTimeout(() => setAddSuccess(""), 4000);
    } catch (err) {
      setAddErr(err.response?.data?.message || "حدث خطأ أثناء الإضافة");
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await Axios.delete(ADMIN_DELETE_ADMIN(pendingDelete.id));
      setAdmins((prev) => prev.filter((a) => a.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (err) {
      alert(err.response?.data?.message || "حدث خطأ أثناء الحذف");
    } finally {
      setDeleting(false);
    }
  }

  /* ────────────────── UI ────────────────── */
  return (
    <div style={{ padding: "28px 24px", maxWidth: 1100, margin: "0 auto", direction: "rtl" }}>
      <h1 style={{ fontSize: "1.45rem", fontWeight: 700, color: "#111", marginBottom: 4 }}>الإعدادات</h1>
      <p style={{ color: "#6b7280", fontSize: "0.88rem", marginBottom: 20 }}>إدارة حسابات المدراء والصلاحيات</p>

      {/* ── My account card (full width) ── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e5e7eb", padding: "16px 22px", marginBottom: 22, display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(135deg,#00a74f,#007a38)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: "1.15rem", flexShrink: 0 }}>
          {currentUser.charAt(0).toUpperCase()}
        </div>
        <div>
          <p style={{ fontWeight: 700, fontSize: "0.95rem", color: "#111", margin: "0 0 4px" }}>{currentUser}</p>
          <RoleBadge role={currentRole} />
        </div>
        <p style={{ marginRight: "auto", marginBottom: 0, fontSize: "0.78rem", color: "#9ca3af" }}>
          {isSuperAdmin ? "لديك صلاحيات إدارة كاملة" : "يمكنك إدارة المحتوى — الحذف للسوبر أدمن فقط"}
        </p>
      </div>

      {/* ── Two-column layout: admins list (right) | add form (left) ── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isSuperAdmin ? "1fr 1fr" : "1fr",
        gap: 20,
        alignItems: "start",
      }}>

        {/* RIGHT column — Admins list (superadmin only) */}
        {isSuperAdmin && (
          <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e5e7eb", overflow: "hidden" }}>
            <div style={{ padding: "15px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h2 style={{ fontSize: "0.97rem", fontWeight: 700, color: "#111", margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
                <svg width="16" height="16" fill="#6b7280" viewBox="0 0 24 24">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                </svg>
                جميع المدراء
              </h2>
              <span style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
                {loadingAdmins ? "..." : `${admins.length} حساب`}
              </span>
            </div>

            {loadingAdmins ? (
              <div style={{ padding: 36, textAlign: "center", color: "#9ca3af", fontSize: "0.88rem" }}>جاري التحميل...</div>
            ) : admins.length === 0 ? (
              <div style={{ padding: 36, textAlign: "center", color: "#9ca3af", fontSize: "0.88rem" }}>لا يوجد مدراء</div>
            ) : (
              admins.map((admin, i) => {
                const isSelf = admin.user_name === currentUser;
                return (
                  <div key={admin.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", borderBottom: i < admins.length - 1 ? "1px solid #f9fafb" : "none", background: isSelf ? "#fafafa" : "#fff" }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: admin.Role === "superadmin" ? "linear-gradient(135deg,#f59e0b,#d97706)" : "linear-gradient(135deg,#6b7280,#4b5563)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: "0.9rem", flexShrink: 0 }}>
                      {admin.user_name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 600, color: "#111", fontSize: "0.88rem", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {admin.user_name}
                        {isSelf && <span style={{ fontSize: "0.68rem", color: "#00a74f", fontWeight: 600, marginRight: 6 }}>(أنت)</span>}
                      </span>
                      <div style={{ marginTop: 3 }}>
                        <RoleBadge role={admin.Role} />
                      </div>
                    </div>
                    <span style={{ fontSize: "0.7rem", color: "#d1d5db", flexShrink: 0 }}>
                      {new Date(admin.createdAt).toLocaleDateString("ar-SY")}
                    </span>
                    {!isSelf && (
                      <button
                        onClick={() => setPendingDelete(admin)}
                        style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 7, border: "1.5px solid #fca5a5", background: "#fff", color: "#dc2626", fontWeight: 600, fontSize: "0.75rem", cursor: "pointer", flexShrink: 0 }}
                      >
                        <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                        حذف
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* LEFT column — Add admin form */}
        <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e5e7eb", padding: "22px" }}>
        <h2 style={{ fontSize: "1rem", fontWeight: 700, color: "#111", marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="17" height="17" fill="#00a74f" viewBox="0 0 24 24">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
          </svg>
          إضافة مدير جديد
        </h2>

        <form onSubmit={handleAdd}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#374151", marginBottom: 6 }}>اسم المستخدم</label>
              <input
                type="text"
                value={form.user_name}
                onChange={(e) => { setForm((p) => ({ ...p, user_name: e.target.value })); setAddErr(""); }}
                placeholder="admin2"
                style={{ width: "100%", padding: "9px 12px", borderRadius: 9, border: "1.5px solid #e5e7eb", fontSize: "0.88rem", outline: "none", direction: "ltr" }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#374151", marginBottom: 6 }}>كلمة المرور</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPwd ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => { setForm((p) => ({ ...p, password: e.target.value })); setAddErr(""); }}
                  placeholder="••••••••"
                  style={{ width: "100%", padding: "9px 36px 9px 12px", borderRadius: 9, border: "1.5px solid #e5e7eb", fontSize: "0.88rem", outline: "none", direction: "ltr" }}
                />
                <button type="button" onClick={() => setShowPwd((p) => !p)} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 0, display: "flex" }}>
                  {showPwd
                    ? <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/></svg>
                    : <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
                  }
                </button>
              </div>
            </div>
          </div>

          {/* Role — superadmin sees both options, admin sees only "admin" */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#374151", marginBottom: 10 }}>نوع الحساب</label>
            <div style={{ display: "flex", gap: 12 }}>
              {(isSuperAdmin
                ? [
                    { val: "admin",      label: "أدمن",      desc: "إدارة المحتوى فقط" },
                    { val: "superadmin", label: "سوبر أدمن", desc: "صلاحيات كاملة" },
                  ]
                : [{ val: "admin", label: "أدمن", desc: "إدارة المحتوى فقط" }]
              ).map((opt) => (
                <label key={opt.val} style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, padding: "11px 15px", borderRadius: 10, border: `1.5px solid ${form.Role === opt.val ? "#00a74f" : "#e5e7eb"}`, background: form.Role === opt.val ? "#f0fdf4" : "#fff", cursor: "pointer" }}>
                  <input type="radio" name="Role" value={opt.val} checked={form.Role === opt.val} onChange={(e) => setForm((p) => ({ ...p, Role: e.target.value }))} style={{ accentColor: "#00a74f" }} />
                  <div>
                    <span style={{ fontWeight: 700, fontSize: "0.86rem", color: form.Role === opt.val ? "#00a74f" : "#374151" }}>{opt.label}</span>
                    <span style={{ display: "block", fontSize: "0.71rem", color: "#9ca3af", marginTop: 1 }}>{opt.desc}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {addErr && (
            <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "9px 14px", color: "#dc2626", fontSize: "0.84rem", marginBottom: 14 }}>
              {addErr}
            </div>
          )}
          {addSuccess && (
            <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "9px 14px", color: "#16a34a", fontSize: "0.84rem", marginBottom: 14 }}>
              {addSuccess}
            </div>
          )}

          <button
            type="submit"
            disabled={adding}
            style={{ padding: "10px 28px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#00a74f,#007a38)", color: "#fff", fontWeight: 700, fontSize: "0.88rem", cursor: adding ? "not-allowed" : "pointer", opacity: adding ? 0.75 : 1 }}
          >
            {adding ? "جاري الإضافة..." : "إضافة المدير"}
          </button>
        </form>
        </div>
        {/* end grid */}
      </div>

      {/* ── Delete confirm modal ── */}
      {pendingDelete && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, backdropFilter: "blur(2px)" }} onClick={() => !deleting && setPendingDelete(null)}>
          <div style={{ background: "#fff", borderRadius: 20, padding: "34px 30px", maxWidth: 390, width: "90%", textAlign: "center", direction: "rtl", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
              <svg width="28" height="28" fill="#dc2626" viewBox="0 0 24 24">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
            </div>
            <h3 style={{ fontWeight: 700, fontSize: "1.05rem", marginBottom: 8, color: "#111" }}>حذف المدير</h3>
            <p style={{ color: "#6b7280", fontSize: "0.88rem", marginBottom: 5 }}>
              هل أنت متأكد من حذف <strong style={{ color: "#111" }}>"{pendingDelete.user_name}"</strong>؟
            </p>
            <p style={{ color: "#ef4444", fontSize: "0.78rem", marginBottom: 26 }}>لا يمكن التراجع عن هذا الإجراء.</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setPendingDelete(null)} disabled={deleting} style={{ flex: 1, padding: "10px 0", borderRadius: 11, border: "1.5px solid #e5e7eb", background: "#fff", fontWeight: 600, fontSize: "0.9rem", cursor: "pointer", color: "#374151" }}>
                إلغاء
              </button>
              <button onClick={handleDelete} disabled={deleting} style={{ flex: 1, padding: "10px 0", borderRadius: 11, border: "none", background: "linear-gradient(135deg,#dc2626,#b91c1c)", color: "#fff", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer", opacity: deleting ? 0.7 : 1 }}>
                {deleting ? "جاري الحذف..." : "نعم، احذف"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
